/**
 * Cosmic Concierge Service
 * API service for AI chat, compatibility, gifting, timing, journal, and checkout.
 * Uses the same API Gateway base URL as transitCalendarService.
 */

const API_BASE = 'https://pxq7fyhjsf.execute-api.eu-west-1.amazonaws.com/prod';

/**
 * Get auth token from localStorage (same key as transitCalendarService).
 */
const getAuthToken = () => localStorage.getItem('transit-calendar-token');

/**
 * Build headers with optional auth token.
 */
const buildHeaders = (includeAuth = true) => {
  const headers = { 'Content-Type': 'application/json' };
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
};

/**
 * Generic request helper with error handling.
 */
const request = async (method, path, body = null, params = null) => {
  let url = `${API_BASE}${path}`;

  if (params) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
    ).toString();
    if (qs) url += `?${qs}`;
  }

  const options = {
    method,
    headers: buildHeaders(),
  };

  if (body && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, options);
    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || data.message || `Request failed (${response.status})`);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    if (error.status) throw error; // Already formatted
    console.error(`[CosmicAPI] ${method} ${path} failed:`, error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const cosmicAPI = {
  // -- AI Chat --
  sendMessage: (message, conversationId, userId) =>
    request('POST', '/cosmic/chat', { message, conversationId, userId }),

  // -- Daily Briefing --
  getDailyBriefing: (userId, date) =>
    request('GET', '/cosmic/briefing', null, { userId, date }),

  // -- Compatibility --
  checkCompatibility: (person1, person2) =>
    request('POST', '/cosmic/compatibility', { person1, person2 }),

  sendCompatibilityInvite: (userId, recipientPhone, recipientEmail) =>
    request('POST', '/cosmic/compatibility/invite', { userId, recipientPhone, recipientEmail }),

  // Full report. mode 'paid' (default) creates a record that waits for Stripe (201 { reportId, status: 'awaiting_payment' });
  // mode 'review' (site owner, logged in) writes it at once (202 { reportId, status: 'queued' }). Poll GET until ready or failed.
  requestCompatibilityReport: (person1, person2, relationshipType = 'romantic', mode = 'paid', consent = null) =>
    request('POST', '/cosmic/compatibility/report', { person1, person2, relationshipType, mode, ...(consent ? { consent } : {}) }),

  // Site owner only: write a failed report again.
  retryCompatibilityReport: (reportId) =>
    request('POST', '/cosmic/compatibility/report', { action: 'retry', reportId }),

  getCompatibilityReport: (reportId) =>
    request('GET', '/cosmic/compatibility/report', null, { id: reportId }),

  // -- Timing Windows --
  getTimingWindows: (userId, topic, startDate, endDate) =>
    request('POST', '/cosmic/timing', { userId, topic, startDate, endDate }),

  // -- Gifts --
  redeemGift: (giftCode) =>
    request('POST', '/cosmic/gift/redeem', { giftCode }),

  // -- Stripe Checkout --
  createCheckout: (productType, userId, email, giftData, extra = {}) =>
    request('POST', '/cosmic/checkout', { productType, userId, email, giftData, ...extra }),

  // -- User Profile --
  getProfile: (userId) =>
    request('GET', '/cosmic/profile', null, { userId, action: 'get' }),

  updateProfile: (userId, data) =>
    request('PUT', '/cosmic/profile', { action: 'update', userId, data }),

  updateChannelPreferences: (userId, preferences) =>
    request('PUT', '/cosmic/profile', { action: 'update', userId, data: { channelPreferences: preferences } }),

  // -- Journal --
  // The journal lives on the profile record (ivy-user-profile lambda appends to
  // journalEntries). /cosmic/journal was routed to the webhook gateway, which
  // rejects it, so both calls go through /cosmic/profile.
  addJournalEntry: (userId, entry) =>
    request('PUT', '/cosmic/profile', { action: 'update', userId, data: { journalEntry: entry } }),

  getJournal: (userId) =>
    request('GET', '/cosmic/profile', null, { userId, action: 'get' }).then((r) => r?.profile?.journalEntries || []),

  // -- Shareable Card --
  createCard: (readingData, format, template) =>
    request('POST', '/cosmic/card', { readingData, format, template }),

  // -- Calculate transits (free preview) --
  getTransits: (date, birthData) =>
    request('POST', '/cosmic/transits', { date, birthData }),

  // -- Generate AI reading from transit data --
  generateReading: (date, transits, birthData, format) =>
    request('POST', '/cosmic/briefing', { date, transits, birthData, format }),
};

export default cosmicAPI;
